import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore
} from "react";

import type {
  ProfileRepositoryBootstrap,
  ProfileRepositoryGeneration,
  ProfileSessionCommands,
  ProfileSessionMachine,
  ProfileSessionNavigate,
  ProfileSessionRoute,
  ProfileSessionSnapshot
} from "./contracts";
import { createProfileSessionMachine } from "./profile-session";

export type { ProfileSessionNavigate } from "./contracts";

export interface UseProfileSessionOptions {
  readonly bootstrapRepository: ProfileRepositoryBootstrap;
  readonly navigate: ProfileSessionNavigate;
  readonly repositoryGeneration: ProfileRepositoryGeneration;
  readonly route: ProfileSessionRoute;
}

export type UseProfileSessionResult = ProfileSessionSnapshot &
  ProfileSessionCommands;

const inactiveRoute: ProfileSessionRoute = {
  kind: "other",
  pathname: ""
};

const ignoreNavigation: ProfileSessionNavigate = () => undefined;

export function useProfileSession({
  bootstrapRepository,
  navigate,
  repositoryGeneration,
  route
}: UseProfileSessionOptions): UseProfileSessionResult {
  const [machine] = useState<ProfileSessionMachine>(
    createProfileSessionMachine
  );
  const forwardNavigation = useCallback<ProfileSessionNavigate>(
    (intent) => {
      navigate(intent);
    },
    [navigate]
  );

  useEffect(() => {
    machine.activate({
      navigate: ignoreNavigation,
      route: inactiveRoute
    });

    return () => {
      machine.dispose();
    };
  }, [machine]);

  useEffect(() => {
    machine.setNavigate(forwardNavigation);
  }, [forwardNavigation, machine]);

  useEffect(() => {
    machine.setRoute(route);
  }, [machine, route]);

  useEffect(() => {
    machine.setRepositorySource(
      repositoryGeneration,
      bootstrapRepository
    );
  }, [bootstrapRepository, machine, repositoryGeneration]);

  const snapshot = useSyncExternalStore(
    machine.subscribe,
    machine.getSnapshot,
    machine.getSnapshot
  );
  const commands = useMemo(
    () => machine.bindCommands(snapshot.repositoryGeneration),
    [machine, snapshot.repositoryGeneration]
  );

  return useMemo(
    () => ({
      ...snapshot,
      ...commands
    }),
    [commands, snapshot]
  );
}
